import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, MenuItem, Alert, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import { Save, ArrowBack, Add, Delete, Receipt } from '@mui/icons-material';
import { salesService } from '../../services/sales.service';
import { customerService } from '../../services/customer.service';
import api from '../../services/api';
import settingsService from '../../services/settings.service';
import { useLanguage } from '../../i18n/LanguageContext';

interface SOLine {
  productId: number | '';
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  taxRate: number;
  lineTotal: number;
}

const emptyLine: SOLine = { productId: '', productName: '', quantity: 1, unitPrice: 0, discount: 0, taxRate: 0, lineTotal: 0 };

const SalesOrderForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isView = Boolean(id);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [viewData, setViewData] = useState<any>(null);
  const [form, setForm] = useState({
    customerId: '' as any,
    shippingAddress: '',
    billingAddress: '',
    notes: '',
    currencyCode: 'USD',
    exchangeRate: 1
  });
  const [lines, setLines] = useState<SOLine[]>([{ ...emptyLine }]);

  useEffect(() => { loadData(); if (isView) loadOrder(); }, []);

  const loadData = async () => {
    try {
      const [custRes, prodRes] = await Promise.all([
        customerService.getAll(),
        api.get('/products'),
      ]);
      setCustomers(custRes);
      setProducts(prodRes.data);
    } catch (err) { console.error('Error loading base data:', err); }

    try {
      const currRes = await settingsService.getCurrencies();
      setCurrencies(currRes.data);
      const def = currRes.data.find((c: any) => c.isDefault);
      if (def && !isView) setForm(f => ({ ...f, currencyCode: def.code }));
    } catch (err) { console.error('Currencies not available:', err); }
  };

  const loadOrder = async () => {
    try {
      const res = await api.get(`/salesorders/${id}`);
      const data = res.data;
      setViewData(data);
      setForm({
        customerId: data.customerId, shippingAddress: data.shippingAddress || '',
        billingAddress: data.billingAddress || '', notes: data.notes || '',
        currencyCode: data.currencyCode || 'USD', exchangeRate: data.exchangeRate || 1
      });
      if (data.orderLines) setLines(data.orderLines.map((l: any) => ({
        productId: l.productId, productName: l.productName,
        quantity: l.quantity, unitPrice: l.unitPrice,
        discount: l.discount || 0, taxRate: l.taxRate || 0,
        lineTotal: l.lineTotal || 0
      })));
    } catch (err) { console.error(err); setError('Failed to load sales order'); }
  };

  const updateLine = (index: number, field: keyof SOLine, value: any) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'productId') {
      const product = products.find((p: any) => p.id === value);
      if (product) {
        updated[index].productName = product.name;
        updated[index].unitPrice = product.unitPrice || 0;
      }
    }
    const lt = updated[index].quantity * updated[index].unitPrice - updated[index].discount;
    updated[index].lineTotal = lt + lt * updated[index].taxRate / 100;
    setLines(updated);
  };

  const addLine = () => setLines([...lines, { ...emptyLine }]);
  const removeLine = (i: number) => { if (lines.length > 1) setLines(lines.filter((_, idx) => idx !== i)); };

  const subTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice - l.discount, 0);
  const taxTotal = lines.reduce((s, l) => { const net = l.quantity * l.unitPrice - l.discount; return s + net * l.taxRate / 100; }, 0);
  const grandTotal = subTotal + taxTotal;

  const sym = currencies.find(c => c.code === form.currencyCode)?.symbol || '';

  const handleStatusChange = async (status: string) => {
    if (!window.confirm(`${t('common.status')}: ${status}?`)) return;
    try {
      await salesService.updateOrderStatus(Number(id), status);
      loadOrder();
    } catch (err: any) { alert(err.response?.data?.message || 'Error updating status'); }
  };

  const handleGenerateInvoice = async () => {
    if (!window.confirm(t('sales_orders.generate_invoice') + '?')) return;
    try {
      const result = await salesService.generateInvoiceFromSO(Number(id));
      alert(result.message);
      navigate('/invoices');
    } catch (err: any) { alert(err.response?.data?.message || 'Error generating invoice'); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validLines = lines.filter(l => l.productId && l.quantity > 0);
    if (validLines.length === 0) { setError(t('common.add_line')); return; }

    try {
      await salesService.createOrder({
        customerId: form.customerId,
        shippingAddress: form.shippingAddress,
        billingAddress: form.billingAddress,
        notes: form.notes,
        currencyCode: form.currencyCode,
        exchangeRate: form.exchangeRate,
        orderLines: validLines.map(l => ({
          productId: l.productId,
          productName: l.productName,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          discount: l.discount,
          taxRate: l.taxRate,
          lineTotal: l.lineTotal
        }))
      } as any);
      navigate('/sales-orders');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creating sales order');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{isView ? `${t('sales_orders.title')}: ${viewData?.orderNumber || ''}` : t('sales_orders.new')}</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/sales-orders')}>{t('common.back')}</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {viewData && <Alert severity={viewData.status === 'DELIVERED' ? 'success' : viewData.status === 'CANCELLED' ? 'error' : 'info'} sx={{ mb: 2 }}>{t('common.status')}: {viewData.status}</Alert>}

      {isView && viewData && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {viewData.status === 'DRAFT' && (
            <Button variant="contained" color="info" size="small" onClick={() => handleStatusChange('CONFIRMED')}>{t('sales_orders.confirm')}</Button>
          )}
          {viewData.status === 'CONFIRMED' && (<>
            <Button variant="contained" color="primary" size="small" onClick={() => handleStatusChange('PROCESSING')}>{t('sales_orders.process')}</Button>
            <Button variant="contained" color="secondary" size="small" startIcon={<Receipt />} onClick={handleGenerateInvoice}>{t('sales_orders.generate_invoice')}</Button>
          </>)}
          {viewData.status === 'PROCESSING' && (<>
            <Button variant="contained" color="warning" size="small" onClick={() => handleStatusChange('SHIPPED')}>{t('sales_orders.ship')}</Button>
            <Button variant="contained" color="secondary" size="small" startIcon={<Receipt />} onClick={handleGenerateInvoice}>{t('sales_orders.generate_invoice')}</Button>
          </>)}
          {viewData.status === 'SHIPPED' && (<>
            <Button variant="contained" color="success" size="small" onClick={() => handleStatusChange('DELIVERED')}>{t('sales_orders.deliver')}</Button>
            <Button variant="contained" color="secondary" size="small" startIcon={<Receipt />} onClick={handleGenerateInvoice}>{t('sales_orders.generate_invoice')}</Button>
          </>)}
          {viewData.status === 'DELIVERED' && (
            <Button variant="contained" color="secondary" size="small" startIcon={<Receipt />} onClick={handleGenerateInvoice}>{t('sales_orders.generate_invoice')}</Button>
          )}
          {viewData.status !== 'CANCELLED' && viewData.status !== 'DELIVERED' && (
            <Button variant="outlined" color="error" size="small" onClick={() => handleStatusChange('CANCELLED')}>{t('common.cancel')}</Button>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required select label={t('sales_orders.customer')} value={form.customerId}
          onChange={e => setForm({ ...form, customerId: e.target.value })} disabled={isView}>
          <MenuItem value="">{t('common.select')}</MenuItem>
          {customers.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.code} - {c.name}</MenuItem>)}
        </TextField>
        <TextField select label={t('common.currency')} value={form.currencyCode} sx={{ minWidth: 150 }}
          onChange={e => setForm({ ...form, currencyCode: e.target.value })} disabled={isView}>
          {currencies.map((c: any) => <MenuItem key={c.id} value={c.code}>{c.code} ({c.symbol})</MenuItem>)}
          {currencies.length === 0 && <MenuItem value="USD">USD</MenuItem>}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth label={t('sales_orders.shipping_address')} value={form.shippingAddress}
          onChange={e => setForm({ ...form, shippingAddress: e.target.value })} disabled={isView} />
        <TextField fullWidth label={t('sales_orders.billing_address')} value={form.billingAddress}
          onChange={e => setForm({ ...form, billingAddress: e.target.value })} disabled={isView} />
      </Box>

      <TextField fullWidth label={t('common.notes')} value={form.notes}
        onChange={e => setForm({ ...form, notes: e.target.value })} sx={{ mb: 2 }} disabled={isView} />

      <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>{t('sales_orders.order_lines')}</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>{t('sales_orders.product')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.quantity')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.unit_price')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('sales_orders.discount')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('sales_orders.tax_pct')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.total')}</TableCell>
              {!isView && <TableCell sx={{ width: '50px' }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  {isView ? line.productName : (
                    <TextField select fullWidth size="small" value={line.productId}
                      onChange={e => updateLine(idx, 'productId', e.target.value)}>
                      <MenuItem value="">{t('common.select')}</MenuItem>
                      {products.map((p: any) => <MenuItem key={p.id} value={p.id}>{p.sku} - {p.name}</MenuItem>)}
                    </TextField>
                  )}
                </TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.quantity}
                  onChange={e => updateLine(idx, 'quantity', Number(e.target.value))} inputProps={{ min: 1 }} disabled={isView} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.unitPrice}
                  onChange={e => updateLine(idx, 'unitPrice', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} disabled={isView} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.discount}
                  onChange={e => updateLine(idx, 'discount', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} disabled={isView} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.taxRate}
                  onChange={e => updateLine(idx, 'taxRate', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} disabled={isView} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{line.lineTotal.toFixed(2)}</TableCell>
                {!isView && <TableCell>
                  <IconButton size="small" color="error" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>
                    <Delete fontSize="small" /></IconButton>
                </TableCell>}
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={4} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('common.subtotal')}:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{subTotal.toFixed(2)}</TableCell>{!isView && <TableCell />}
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={4} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('common.tax')}:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{taxTotal.toFixed(2)}</TableCell>{!isView && <TableCell />}
            </TableRow>
            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
              <TableCell colSpan={4} /><TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{t('common.total')}:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{grandTotal.toFixed(2)}</TableCell>{!isView && <TableCell />}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {!isView && <>
        <Button startIcon={<Add />} onClick={addLine} sx={{ mt: 1 }}>{t('common.add_line')}</Button>
        <Box sx={{ mt: 3 }}>
          <Button type="submit" variant="contained" startIcon={<Save />} size="large">{t('sales_orders.create')}</Button>
        </Box>
      </>}
    </Box>
  );
};

export default SalesOrderForm;
